import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { CatalogApiService } from '@core/api/services/catalog-api.service';
import { OrganizationUnitApiService } from '@core/api/services/organization-unit-api.service';
import { PortalImportEquivalence, PortalMemberImportService } from '@core/api/services/portal-member-import.service';
import { PageHeaderComponent } from '@shared/components';
import { FilterChip, PaginationConfig, SortEntry, TableColumn } from '@shared/components/data-table/data-table.component';
import { DataTableMultiSelectFilterComponent } from '@shared/components/data-table-multi-select-filter/data-table-multi-select-filter.component';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';
import { NotificationService } from '@shared/services/notification.service';
import { DeleteConfirmDialogComponent } from '../../../user/components/shared/delete-confirm-dialog/delete-confirm-dialog.component';
import { ActivityManagementService } from '../../services/activity-management.service';
import { AssignmentManagementService } from '../../services/assignment-management.service';
import { MinistryAppointmentManagementService } from '../../services/ministry-appointment-management.service';
import { EquivalenceCatalogs, PortalImportEquivalenceEditorComponent, SECTION_LABELS, TARGET_LABELS, UNIT_LABELS } from './portal-import-equivalence-editor.component';

interface EquivalenceRow extends PortalImportEquivalence {
  sectionLabel: string;
  destinationLabel: string;
  targetLabel: string;
  unitLabel: string;
}

@Component({
  selector: 'app-portal-import-equivalences',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatCardModule, MatDialogModule, MatIconModule, MatTooltipModule, PageHeaderComponent, TableSurfaceComponent, DataTableMultiSelectFilterComponent],
  templateUrl: './portal-import-equivalences.component.html',
  styleUrl: './portal-import-equivalences.component.scss',
})
export class PortalImportEquivalencesComponent implements OnInit {
  @ViewChild(TableSurfaceComponent) private tableSurface?: TableSurfaceComponent<EquivalenceRow>;
  private readonly destroyRef = inject(DestroyRef);
  private readonly api = inject(PortalMemberImportService);
  private readonly appointmentsApi = inject(MinistryAppointmentManagementService);
  private readonly assignmentsApi = inject(AssignmentManagementService);
  private readonly activitiesApi = inject(ActivityManagementService);
  private readonly catalogApi = inject(CatalogApiService);
  private readonly unitsApi = inject(OrganizationUnitApiService);
  private readonly dialog = inject(MatDialog);
  private readonly notifications = inject(NotificationService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly columns: TableColumn[] = [
    { key: 'sectionLabel', header: 'Sección', sortable: true, hideable: true, resizable: true },
    { key: 'sourceValue', header: 'Valor del Portal', sortable: true, resizable: true, mobileTitle: true },
    { key: 'destinationLabel', header: 'Destino', sortable: true, hideable: true, resizable: true },
    { key: 'targetLabel', header: 'Valor en Comunidades', sortable: true, hideable: true, resizable: true },
    { key: 'unitLabel', header: 'Unidad', sortable: true, hideable: true, resizable: true },
    { key: 'actions', header: 'Acciones', width: '104px', minWidth: '104px', exportable: false },
  ];
  rows: EquivalenceRow[] = [];
  tableData: EquivalenceRow[] = [];
  catalogs: EquivalenceCatalogs = { appointments: [], assignments: [], activities: [], countries: [] };
  loading = false;
  errorMessage = '';
  searchTerm = '';
  deletingId?: number;
  readonly sectionOptions = Object.entries(SECTION_LABELS).map(([value, label]) => ({ value, label }));
  readonly destinationOptions = Object.entries({ ...TARGET_LABELS, ignore: 'Ignorar' }).map(([value, label]) => ({ value, label }));
  readonly unitOptions = Object.entries({ ...UNIT_LABELS, none: 'No aplica' }).map(([value, label]) => ({ value, label }));
  readonly filterForm = new FormGroup({
    section: new FormControl(this.sectionOptions.map(option => option.value), { nonNullable: true }),
    destination: new FormControl(this.destinationOptions.map(option => option.value), { nonNullable: true }),
    unit: new FormControl(this.unitOptions.map(option => option.value), { nonNullable: true }),
  });
  activeFilterChips: FilterChip[] = [];
  sortState: SortEntry = { sortBy: 'sourceValue', direction: 'asc' };
  readonly initialSort: SortEntry = { ...this.sortState };
  paginationConfig: PaginationConfig = { currentPage: 1, pageSize: 10, totalCount: 0, totalPages: 1 };
  private unitNames = new Map<string, string>();

  ngOnInit(): void {
    this.filterForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.paginationConfig = { ...this.paginationConfig, currentPage: 1 };
      this.applyFilter();
      this.cdr.markForCheck();
    });
    this.load();
  }

  load(): void {
    this.loading = true;
    this.errorMessage = '';
    forkJoin({
      rows: this.api.getEquivalences(),
      appointments: this.appointmentsApi.getAll(),
      assignments: this.assignmentsApi.getAll(),
      activities: this.activitiesApi.getAll(),
      countries: this.catalogApi.getCountries(),
    }).pipe(
      switchMap(data => {
        const references = [...new Set(data.rows.filter(row => !row.ignore && row.targetReference).map(row => row.targetReference!))];
        const units = references.length ? forkJoin(references.map(id => this.unitsApi.getById(id).pipe(
          map(unit => ({ id, name: unit.name ?? 'Unidad sin nombre' })),
          catchError(() => of({ id, name: 'Unidad no disponible' })),
        ))) : of([]);
        return units.pipe(map(units => ({ ...data, units })));
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: data => {
        this.unitNames = new Map(data.units.map(unit => [unit.id, unit.name]));
        this.catalogs = {
          appointments: data.appointments.map(item => ({ id: item.id!, name: item.name! })),
          assignments: data.assignments,
          activities: data.activities,
          countries: data.countries.map(item => ({ id: item.id!, name: item.name! })),
        };
        this.rows = data.rows.map(row => ({
          ...row,
          sectionLabel: SECTION_LABELS[row.sourceSection] ?? row.sourceSection,
          destinationLabel: row.ignore ? 'Ignorar' : TARGET_LABELS[row.targetKind] ?? row.targetKind,
          targetLabel: this.targetLabel(row),
          unitLabel: row.ignore || row.sourceSection === 'countries' || row.sourceSection === 'organizationUnits'
            ? '—' : row.unitRule === 'specific' ? this.unitNames.get(row.targetReference ?? '') ?? 'Unidad no disponible'
              : UNIT_LABELS[row.unitRule] ?? row.unitRule,
        }));
        this.applyFilter();
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.errorMessage = 'No se pudieron cargar las equivalencias. Intentá nuevamente.';
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  edit(row?: PortalImportEquivalence): void {
    if (this.loading || this.errorMessage) return;
    this.dialog.open(PortalImportEquivalenceEditorComponent, modalDialogConfig({
      size: 'lg', data: { row, catalogs: this.catalogs },
    })).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(saved => {
      if (saved) this.load();
    });
  }

  onSearch(term: string): void {
    this.searchTerm = term;
    this.paginationConfig = { ...this.paginationConfig, currentPage: 1 };
    this.applyFilter();
  }

  onPageChange(event: { page: number }): void {
    this.paginationConfig = { ...this.paginationConfig, currentPage: event.page };
    this.applyFilter();
  }

  onPageSizeChange(event: { pageSize: number }): void {
    this.paginationConfig = { ...this.paginationConfig, currentPage: 1, pageSize: event.pageSize };
    this.applyFilter();
  }

  onSortChange(sort: SortEntry): void {
    this.sortState = sort;
    this.paginationConfig = { ...this.paginationConfig, currentPage: 1 };
    this.applyFilter();
  }

  onFilterRemove(key: string): void {
    if (key === 'section') this.filterForm.controls.section.setValue(this.sectionOptions.map(option => option.value));
    if (key === 'destination') this.filterForm.controls.destination.setValue(this.destinationOptions.map(option => option.value));
    if (key === 'unit') this.filterForm.controls.unit.setValue(this.unitOptions.map(option => option.value));
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.tableSurface?.clearSearch();
    this.filterForm.setValue({
      section: this.sectionOptions.map(option => option.value),
      destination: this.destinationOptions.map(option => option.value),
      unit: this.unitOptions.map(option => option.value),
    });
  }

  remove(row: PortalImportEquivalence): void {
    this.dialog.open(DeleteConfirmDialogComponent, modalDialogConfig({
      size: 'sm', layout: 'confirm', tone: 'danger',
      data: { title: 'Eliminar equivalencia', message: '¿Querés eliminar esta equivalencia? El valor del Portal deberá volver a resolverse al importar.' },
    })).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(confirmed => {
      if (!confirmed) return;
      this.deletingId = row.id;
      this.cdr.markForCheck();
      this.api.deleteEquivalence(row.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.deletingId = undefined;
          this.notifications.success('Equivalencia eliminada.');
          this.load();
        },
        error: () => {
          this.deletingId = undefined;
          this.notifications.error('No se pudo eliminar la equivalencia.');
          this.cdr.markForCheck();
        },
      });
    });
  }

  private targetLabel(row: PortalImportEquivalence): string {
    if (row.ignore) return '—';
    if (row.targetKind === 'organizationUnit') return this.unitNames.get(row.targetReference ?? '') ?? 'Unidad no disponible';
    const options = row.targetKind === 'country' ? this.catalogs.countries
      : row.targetKind === 'appointment' ? this.catalogs.appointments
      : row.targetKind === 'assignment' ? this.catalogs.assignments : this.catalogs.activities;
    const name = options.find(item => item.id === row.targetId)?.name ?? 'Destino no disponible';
    const role = this.catalogs.activities.find(item => item.id === row.targetId)?.roles.find(item => item.id === row.activityRoleId);
    return row.targetKind === 'activity' && role ? `${name} · ${role.name}` : name;
  }

  private applyFilter(): void {
    const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
    const term = normalize(this.searchTerm.trim());
    const filters = this.filterForm.getRawValue();
    const filteredRows = this.rows.filter(row => {
      const unit = row.ignore || ['countries', 'organizationUnits'].includes(row.sourceSection) ? 'none' : row.unitRule;
      return filters.section.includes(row.sourceSection)
        && filters.destination.includes(row.ignore ? 'ignore' : row.targetKind)
        && filters.unit.includes(unit)
        && normalize([row.sectionLabel, row.sourceValue, row.sourceLabel, row.destinationLabel, row.targetLabel, row.unitLabel].join(' ')).includes(term);
    });
    const { sortBy, direction } = this.sortState;
    filteredRows.sort((left, right) => (direction === 'asc' ? 1 : -1) * String(left[sortBy as keyof EquivalenceRow] ?? '')
      .localeCompare(String(right[sortBy as keyof EquivalenceRow] ?? ''), 'es', { numeric: true, sensitivity: 'base' }));
    const totalCount = filteredRows.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / this.paginationConfig.pageSize));
    const currentPage = Math.max(1, Math.min(this.paginationConfig.currentPage, totalPages));
    this.paginationConfig = { ...this.paginationConfig, currentPage, totalCount, totalPages };
    const start = (currentPage - 1) * this.paginationConfig.pageSize;
    this.tableData = filteredRows.slice(start, start + this.paginationConfig.pageSize);
    this.activeFilterChips = [
      { key: 'section', label: 'Sección', values: filters.section, options: this.sectionOptions },
      { key: 'destination', label: 'Destino', values: filters.destination, options: this.destinationOptions },
      { key: 'unit', label: 'Unidad', values: filters.unit, options: this.unitOptions },
    ].filter(filter => filter.values.length !== filter.options.length).map(filter => ({
      key: filter.key,
      label: `${filter.label}: ${filter.values.length ? filter.options.filter(option => filter.values.includes(option.value)).map(option => option.label).join(', ') : 'Ninguno'}`,
      removable: true,
    }));
  }
}
